<template>
  <div class="approval-container">
    <button @click="handleLogout" class="logout-button">Logout</button>
    <h1 style="margin-top: 50px">Geplante Beiträge</h1>

    <p style="margin-top: 20px; margin-bottom: 40px; color: #666;" :style="{'display': isLoading ? 'none' : 'block', 'font-size': isMobile ? '12px' : '15px'}">
      Alle grün markierten Beiträge gehen an dem genannten Datum ohne weitere Freigabe online.<br>
      Wenn du einen Änderungswunsch hast, drücke auf einen Beitrag und dann unten auf "Korrekturanfrage senden".<br>
      Dadurch werden wir informiert und setzen die Korrektur um, bevor der Beitrag online geht.
    </p>
    

    <div v-if="isLoading" class="loading-view">
      <div class="progress-container">
        <div class="progress-bar">
          <span class="progress-text">{{ Math.floor(loadingProgress) }}%</span>
          <div class="progress-bar-fill" :style="{ width: loadingProgress + '%' }"></div>
        </div>
      </div>
      <h3>Die geplanten Beiträge werden geladen. Das kann ein paar Sekunden dauern.</h3>
    </div>

    <div v-else>
      <div 
        v-for="post in posts" 
        :key="post.postId" 
        style="margin-bottom: 20px"
      >
        <PostComponent 
          :post="post"
          @approve="handleApprove"
          @reject="handleReject"
          @handleClick="openPreview(post)"
        />
      </div>

      <div v-if="posts.length === 0">
        <h3>Es sind aktuell keine Posts geplant</h3>
      </div>
    </div>
    
    <PreviewModal
      :show="showPreview"
      :user="user"
      :post="selectedPost"
      @close="closePreview"
      @approve="handleApprove"
      @reject="handleReject"
      @saveText="handleUpdateText"
    />

  </div>
</template>

<script>
import { defineComponent } from 'vue'
import PostComponent from '../components/PostComponent.vue'
import PreviewModal from '../components/PreviewModal.vue'
import maintenanceService from '../services/maintenanceService.js'

export default defineComponent({
  name: 'ApprovalView',
  components: {
    PostComponent,
    PreviewModal
  },
  data() {
    return {
      posts: [],
      user: {},
      showPreview: false,
      selectedPost: null,
      isLoading: true,
      loadingProgress: 0,
      loadingInterval: null
    }
  },
  async created() {
    this.isLoading = true;
    this.startLoading();
    try {
      let token = localStorage.getItem('approvalSystemToken');
      if (!token) {
        throw new Error('Kein Token gefunden');
      }

      // Warte auf User-Daten
      let user = await fetch(`${process.env.VUE_APP_BASE_URL}/agency/app/user`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'JWT ' + token
        },
      });

      if (!user.ok || user.status === 401) {
        localStorage.removeItem('approvalSystemToken');
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('userData');
        throw new Error('Ungültiger Token');
      }

      this.user = await user.json();

      // Setze Auth-Token im MaintenanceService für SSE-Verbindung
      maintenanceService.setAuthToken(token);

      // Warte auf Posts
      let posts = await fetch(`${process.env.VUE_APP_BASE_URL}/agency/app/approval/all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'JWT ' + token
        },
      });

      if (!posts.ok || posts.status === 401) {
        localStorage.removeItem('approvalSystemToken');
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('userData');
        throw new Error('Fehler beim Laden der Posts');
      }

      let p = await posts.json();
      this.posts = p.posts;
      
      // Generiere Thumbnails für alle Videos
      await this.generateAllThumbnails();
      
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
      if (error.message === 'Kein Token gefunden' || error.message === 'Ungültiger Token') {
        this.$router.push('/login');
      }
    } finally {
      this.finishLoading();
    }
  },
  computed: {
    isMobile() {
      return window.innerWidth <= 768;
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleApprove(postId) {
      const post = this.posts.find(p => p.postId === postId)
      if (post) {
        post.status = 'approved'
      }
      this.showPreview = false
    },
    async handleReject(postId, note) {
      let token =  localStorage.getItem('approvalSystemToken');
      let post = this.posts.find(p => p.postId === postId)
      if (post) {
        await fetch(`${process.env.VUE_APP_BASE_URL}/agency/app/approval/reject`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'authorization': 'JWT ' + token
            },
            body: JSON.stringify({post: post, note: note})
          });
        post.status = 'rejected';
      }
      this.showPreview = false
      this.loadData();
    },
    async handleUpdateText(postId, newText, { resolve, reject }) {
      let token = localStorage.getItem('approvalSystemToken');
      let post = this.posts.find(p => p.postId === postId)
      if (post) {
        try {
          await fetch(`${process.env.VUE_APP_BASE_URL}/agency/app/approval/update`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'authorization': 'JWT ' + token
            },
            body: JSON.stringify({post: post, text: newText})
          });
          post.text = newText;
          resolve(); // Erfolg signalisieren
          // Nach erfolgreichem Update:
          this.showPreview = false; // Schließe das Preview-Modal
          await this.loadData(); // Lade die Posts neu
        } catch (error) {
          console.error('Fehler beim Aktualisieren des Texts:', error);
          reject(error); // Fehler zurückgeben
        }
      }
    },
    openPreview(post) {
      this.selectedPost = post
      this.showPreview = true
    },
    closePreview() {
      this.showPreview = false
      this.selectedPost = null
    },
    async loadData() {
      this.isLoading = true;
      try {
        let token =  localStorage.getItem('approvalSystemToken');
        let posts = await fetch(`${process.env.VUE_APP_BASE_URL}/agency/app/approval/all`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'authorization': 'JWT ' + token
              },
            });
        if(!posts.ok){
          localStorage.removeItem('approvalSystemToken');
          localStorage.removeItem('isAuthenticated');
          localStorage.removeItem('userData');
          throw new Error('error while loding posts');
        }
        if(posts.status == 401){
          localStorage.removeItem('approvalSystemToken')
          localStorage.removeItem('isAuthenticated')
          localStorage.removeItem('userData')
        }
        let p = await posts.json();
        this.posts = p.posts;
      } catch (error) {
        console.error('Fehler beim Laden der Posts:', error)
      } finally {
        this.isLoading = false;
      }
    },
    handleLogout() {
      localStorage.removeItem('approvalSystemToken');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('userData');
      
      // Entferne Auth-Token aus MaintenanceService
      maintenanceService.setAuthToken(null);
      
      this.$router.push('/login');
    },
    startLoading() {
      this.loadingProgress = 0;
      const increment = 80 / (10 * 10); // 80% in 10 Sekunden, 10 Updates pro Sekunde
      this.loadingInterval = setInterval(() => {
        if (this.loadingProgress < 80) {
          this.loadingProgress = Math.min(80, this.loadingProgress + increment);
        }
      }, 100);
    },
    finishLoading() {
      clearInterval(this.loadingInterval);
      this.loadingProgress = 100;
      setTimeout(() => {
        this.isLoading = false;
        this.loadingProgress = 0;
      }, 500);
    },
    handleResize() {
      this.$forceUpdate();
    },
    async generateAllThumbnails() {
      const thumbnailCache = new Map();
      const timeout = 10000; // 10 Sekunden Timeout
      
      for (const post of this.posts) {
        if (post.metricoolPost?.media) {
          const video = post.metricoolPost.media.find(url => url && url.includes('.mp4'));
          if (video && post.metricoolPost.videoCoverMilliseconds) {
            try {
              const thumbnailPromise = this.generateThumbnail(video, post.metricoolPost.videoCoverMilliseconds, thumbnailCache);
              const thumbnail = await Promise.race([
                thumbnailPromise,
                new Promise((_, reject) => 
                  setTimeout(() => reject(new Error('Thumbnail generation timeout')), timeout)
                )
              ]);
              post.videoThumbnail = thumbnail;
            } catch (error) {
              console.error('Fehler beim Generieren des Thumbnails:', error);
              post.videoThumbnail = ''; // Setze leeres Thumbnail bei Fehler
            }
          }
        }
      }
    },
    
    async generateThumbnail(videoUrl, timeInMs, cache) {
      if (!videoUrl || typeof timeInMs !== 'number' || timeInMs < 0) {
        return '';
      }

      // Cache-Key erstellen
      const cacheKey = `${videoUrl}_${timeInMs}`;
      
      // Prüfen ob Thumbnail im Cache ist
      if (cache.has(cacheKey)) {
        return cache.get(cacheKey);
      }
      
      try {
        // Erstelle ein temporäres Video-Element
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.preload = 'metadata';
        video.src = videoUrl;
        
        return new Promise((resolve) => {
          const handleLoadedMetadata = () => {
            video.currentTime = timeInMs / 1000;
            
            const handleSeeked = () => {
              try {
                if (!video.videoWidth || !video.videoHeight) {
                  resolve('');
                  return;
                }
                
                const videoRatio = video.videoWidth / video.videoHeight;
                const width = video.videoWidth;
                const height = width / videoRatio;
                
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d', { alpha: false });
                canvas.width = width;
                canvas.height = height;
                
                context.imageSmoothingEnabled = true;
                context.imageSmoothingQuality = 'high';
                context.drawImage(video, 0, 0, width, height);
                
                const thumbnail = canvas.toDataURL('image/jpeg', 0.85);
                cache.set(cacheKey, thumbnail);
                
                resolve(thumbnail);
              } catch (error) {
                console.error('Fehler beim Erstellen des Thumbnails:', error);
                resolve('');
              } finally {
                video.removeEventListener('seeked', handleSeeked);
                video.removeEventListener('loadedmetadata', handleLoadedMetadata);
              }
            };
            
            video.addEventListener('seeked', handleSeeked);
          };
          
          video.addEventListener('loadedmetadata', handleLoadedMetadata);
          
          video.addEventListener('error', (error) => {
            console.error('Fehler beim Laden des Videos:', error);
            resolve('');
          });
        });
      } catch (error) {
        console.error('Fehler in generateThumbnail:', error);
        return '';
      }
    }
  }
})
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
h3 {
  margin: 40px 0 0;
}
ul {
  list-style-type: none;
  padding: 0;
}
li {
  display: inline-block;
  margin: 0 10px;
}
a {
  color: #42b983;
}
.approval-container {
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
}
.loading-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  text-align: center;
  margin-top: 40px;
}

.progress-container {
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
}

.progress-bar {
  width: 100%;
  height: 30px;
  background-color: #e0e0e0;
  border-radius: 15px;
  overflow: hidden;
  position: relative;
}

.progress-text {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: 16px;
  font-weight: bold;
  color: #333;
  z-index: 2;
  text-shadow: 0px 0px 2px rgba(255, 255, 255, 0.8);
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(
    90deg,
    #42b983 0%,
    #4cd49b 50%,
    #42b983 100%
  );
  background-size: 200% 100%;
  animation: loading 2s linear infinite;
  transition: width 0.1s ease-out;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.logout-button {
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: #e44848;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.3s;
}

.logout-button:hover {
  background-color: #e44848;
}

.loader-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  flex-direction: column;
  text-align: center;
  padding: 20px;
}

.loader {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-radius: 50%;
  border-top: 5px solid #42b983;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media screen and (max-width: 768px) {
  .loading-view {
    margin: 30px auto;
  }

  .progress-container {
    max-width: 280px;
  }

  h3 {
    margin: 20px 0;
    text-align: center;
    padding: 0 20px;
  }
}

/* Fügen Sie einen allgemeinen Button-Style hinzu */
button {
  color: white !important;
}

.action-button, 
.approve-btn, 
.cancel-btn, 
.reject-btn {
  color: white !important;
}
</style>
